import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Upload, Plus, Trash2, Eye, CheckCircle, XCircle, RotateCcw, AlertTriangle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FAMILY = { name: '', age_sex: '', relation: '', occupation: '' };
const EMPTY_EDU = { qualification: '', university: '', year: '', percentage: '', major_subject: '' };
const EMPTY_EXP = { organisation: '', from: '', to: '', designation: '', designation_at_joining: '', job_responsibility: '', supervisor: '', gross_salary: '', reason_for_leaving: '' };
const EMPTY_REF = { name: '', address: '', phone: '' };
const DOC_TYPES = [
  { key: 'aadhaar', label: 'Aadhaar Card' },
  { key: 'pan', label: 'PAN Card' },
  { key: 'address_proof', label: 'Address Proof' },
  { key: 'education_cert', label: 'Education Certificate' },
  { key: 'experience_cert', label: 'Experience / Relieving Letter' },
  { key: 'passport', label: 'Passport Copy' },
  { key: 'photo', label: 'Recent Photo' },
];

const InputField = ({ label, value, onChange, type = 'text', required = false, className = '' }) => (
  <div className={className}><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">{label}</Label>
    <Input type={type} value={value} onChange={onChange} required={required} className="mt-1 rounded border-blue-200 text-sm" /></div>
);

export default function OnboardingForm() {
  const [user, setUser] = useState(null);
  const [existingForms, setExistingForms] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [viewForm, setViewForm] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [formId, setFormId] = useState(null);
  const [personal, setPersonal] = useState({ full_name: '', fathers_name: '', dob: '', weight: '', height: '', current_address: '', permanent_address: '', contact1: '', contact2: '', email: '', aadhaar_no: '', pan_no: '', marital_status: '', blood_group: '', designation: '', grade: '', location: '' });
  const [family, setFamily] = useState([{ ...EMPTY_FAMILY }]);
  const [education, setEducation] = useState([{ ...EMPTY_EDU }]);
  const [experience, setExperience] = useState([{ ...EMPTY_EXP }]);
  const [lastPosition, setLastPosition] = useState({ designation: '', organisation: '', doj: '', designation_at_joining: '', num_employees: '', address: '', business: '', annual_turnover: '', job_responsibility: '', reporting_to_name: '', reporting_to_designation: '', gross_salary: '' });
  const [references, setReferences] = useState([{ ...EMPTY_REF }, { ...EMPTY_REF }]);
  const [additional, setAdditional] = useState({ physical_disabilities: '', marital_indebtness: '', court_proceedings: '', languages_known: '', specialized_training: '', other_info: '', previously_interviewed: '', interview_date: '', interview_position: '', interview_location: '', interview_outcome: '', related_to_employee: '' });
  const [emergency, setEmergency] = useState({ blood_group: '', allergic_to: '', eyesight_left: '', eyesight_right: '', major_illness: '', contact_person: '', contact_address: '', contact_phone: '' });
  const [declaration, setDeclaration] = useState(false);
  const [uploading, setUploading] = useState({});

  useEffect(() => { const ud = localStorage.getItem('user'); if (ud) setUser(JSON.parse(ud)); }, []);

  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const isManagement = user && (user.role === 'admin' || user.role === 'team_leader');

  const fetchForms = async () => {
    try { const res = await axios.get(`${API}/hrms/onboarding-form`, { headers: getHeaders() }); setExistingForms(res.data); } catch (err) { console.error("./pages/OnboardingForm.jsx:", err); }
  };
  useEffect(() => { fetchForms(); }, [user]);

  const handleSubmitForm = async () => {
    if (!declaration) { toast.error('Please accept the declaration'); return; }
    try {
      const payload = { personal, family_details: family, education, experience, last_position: lastPosition, references, additional_info: additional, emergency, declaration_accepted: declaration };
      // Always use POST — backend handles both new submissions and updating triggered forms
      const res = await axios.post(`${API}/hrms/onboarding-form`, payload, { headers: getHeaders() });
      setFormId(res.data.id);
      toast.success('Form submitted! Now upload your documents.');
      setStep(8);
      fetchForms();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
  };

  const handleUploadDoc = async (docType, file) => {
    if (!formId) return;
    setUploading(prev => ({ ...prev, [docType]: true }));
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/hrms/onboarding-form/${formId}/upload-document?doc_type=${docType}`, fd, { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success(`${docType} uploaded`);
      fetchForms();
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(prev => ({ ...prev, [docType]: false })); }
  };

  const handleStatusUpdate = async (fId, status, reason = '') => {
    try {
      await axios.put(`${API}/hrms/onboarding-form/${fId}/status`, { status, reason }, { headers: getHeaders() });
      toast.success(status === 'rejected' ? 'Form sent back for resubmission' : `Form ${status}`);
      fetchForms();
      setRejectOpen(false);
      setRejectReason('');
    } catch (e) { toast.error('Failed to update status'); }
  };

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectFormId, setRejectFormId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const addRow = (arr, setArr, template) => setArr([...arr, { ...template }]);
  const removeRow = (arr, setArr, idx) => setArr(arr.filter((_, i) => i !== idx));
  const updateRow = (arr, setArr, idx, field, value) => { const n = [...arr]; n[idx][field] = value; setArr(n); };

  const STEPS = ['Personal', 'Family', 'Education', 'Experience', 'Last Position', 'References', 'Additional', 'Emergency & Declaration', 'Documents'];

  // If user has existing form and not filling new one
  if (step === 0 && !formId) {
    const myForm = existingForms.find(f => f.user_id === user?.id);
    const isTriggeredButNotFilled = myForm && myForm.status === 'pending' && !myForm.declaration_accepted;
    const isRejected = myForm && myForm.status === 'rejected';

    // Rejected — show reason and allow resubmission
    if (isRejected && !isManagement) {
      return (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="onboarding-form-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Onboarding Form</h1>
              <p className="text-slate-600">Employee joining report & information sheet</p>
            </div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-red-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-bold text-red-800 text-base">Your onboarding form has been sent back for resubmission</p>
                <p className="text-sm text-red-700 mt-2 bg-red-100 rounded p-3"><span className="font-semibold">Reason:</span> {myForm.rejection_reason || 'No reason provided'}</p>
                {myForm.rejected_by && <p className="text-xs text-red-500 mt-2">Rejected by {myForm.rejected_by} on {myForm.rejected_at ? new Date(myForm.rejected_at).toLocaleDateString() : ''}</p>}
              </div>
            </div>
          </div>
          <Button data-testid="start-form-btn" onClick={() => { setFormId(myForm.id); setStep(1); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
            <RotateCcw size={16} className="mr-2" />Resubmit Joining Form
          </Button>
        </div>
      );
    }

    // If form was triggered by admin but not yet filled, auto-start filling
    if (isTriggeredButNotFilled && !isManagement) {
      return (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="onboarding-form-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Onboarding Form</h1>
              <p className="text-slate-600">Employee joining report & information sheet</p>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
            <p className="font-bold text-blue-800">Your onboarding form is ready to be filled</p>
            <p className="text-sm text-blue-700 mt-1">Please complete all sections and submit your joining information.</p>
          </div>
          <Button data-testid="start-form-btn" onClick={() => { setFormId(myForm.id); setStep(1); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold"><Plus size={16} className="mr-2" />Fill Joining Form</Button>
        </div>
      );
    }

    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="onboarding-form-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Onboarding Form</h1>
            <p className="text-slate-600">Employee joining report & information sheet</p>
          </div>
          {!myForm && !isManagement && <Button data-testid="start-form-btn" onClick={() => setStep(1)} className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold"><Plus size={16} className="mr-2" />Fill Joining Form</Button>}
        </div>

        {myForm && myForm.declaration_accepted && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-4 mb-6">
            <p className="font-bold text-emerald-800">Your onboarding form has been submitted</p>
            <p className="text-sm text-emerald-700 mt-1">Status: <span className="font-bold uppercase">{myForm.status}</span></p>
          </div>
        )}

        {isManagement && existingForms.length === 0 && (
          <div className="bg-white border border-blue-100 rounded-lg shadow-sm p-10 text-center">
            <FileText size={40} className="mx-auto text-blue-300 mb-3" />
            <p className="font-bold text-blue-900 text-base mb-1">No onboarding forms yet</p>
            <p className="text-sm text-slate-500 mb-5 max-w-md mx-auto">
              Trigger an onboarding form for any employee from the Employee Directory. Once triggered, the employee will be asked to fill out their joining information and upload required documents.
            </p>
            <Button
              data-testid="goto-employee-directory-btn"
              onClick={() => { window.location.href = '/employee-directory'; }}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold"
            >
              Go to Employee Directory
            </Button>
          </div>
        )}

        {isManagement && existingForms.length > 0 && (
          <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
            <div className="p-4 border-b border-blue-100 flex items-center justify-between gap-3">
              <p className="font-bold text-blue-900">Submitted Forms</p>
              <div className="relative flex-1 max-w-xs">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="onboarding-search-input" placeholder="Search by employee or status…" value={searchText} onChange={e => setSearchText(e.target.value)} className="pl-9 h-9 rounded border-blue-200 text-sm" />
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-blue-50">
                  <TableHead className="font-bold text-blue-900">Employee</TableHead>
                  <TableHead className="font-bold text-blue-900">Submitted</TableHead>
                  <TableHead className="font-bold text-blue-900">Status</TableHead>
                  <TableHead className="font-bold text-blue-900">Documents</TableHead>
                  <TableHead className="font-bold text-blue-900">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const q = searchText.trim().toLowerCase();
                  const filteredForms = q ? existingForms.filter(f =>
                    (f.user_name || '').toLowerCase().includes(q) ||
                    (f.status || '').toLowerCase().includes(q)
                  ) : existingForms;
                  return filteredForms.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">No forms match your search.</TableCell></TableRow>
                  ) : filteredForms.map(f => (
                  <TableRow key={f.id} data-testid={`form-row-${f.id}`} className="hover:bg-blue-50">
                    <TableCell className="font-semibold text-blue-900">{f.user_name}</TableCell>
                    <TableCell className="text-sm">{new Date(f.created_at).toLocaleDateString()}</TableCell>
                    <TableCell><span className={`px-2 py-1 text-xs font-bold uppercase rounded ${f.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : f.status === 'submitted' ? 'bg-amber-100 text-amber-800' : f.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-800'}`}>{f.status}</span></TableCell>
                    <TableCell className="text-xs">{Object.keys(f.documents || {}).length} uploaded</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" data-testid={`view-form-${f.id}`} onClick={() => { setViewForm(f); setViewOpen(true); }} className="text-blue-600 hover:bg-blue-50"><Eye size={14} className="mr-1" />View</Button>
                        {f.status === 'submitted' && (
                          <>
                            <Button size="sm" data-testid={`approve-form-${f.id}`} onClick={() => handleStatusUpdate(f.id, 'approved')} className="bg-emerald-600 text-white text-xs h-7 rounded"><CheckCircle size={12} className="mr-1" />Approve</Button>
                            <Button size="sm" data-testid={`reject-form-${f.id}`} onClick={() => { setRejectFormId(f.id); setRejectOpen(true); }} className="bg-red-600 text-white text-xs h-7 rounded"><XCircle size={12} className="mr-1" />Reject</Button>
                          </>
                        )}
                        {f.status === 'rejected' && (
                          <span className="text-xs text-red-500 italic ml-1">Sent back</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ));
                })()}
              </TableBody>
            </Table>
          </div>
        )}

        {/* View Form Dialog */}
        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          <DialogContent className="rounded max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-blue-900">Joining Form — {viewForm?.user_name}</DialogTitle>
              <DialogDescription>Submitted on {viewForm ? new Date(viewForm.created_at).toLocaleDateString() : ''}</DialogDescription>
            </DialogHeader>
            {viewForm && (
              <div className="space-y-6 mt-4 text-sm">
                <Section title="Personal Details">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(viewForm.personal || {}).map(([k, v]) => v && <div key={k}><span className="text-xs text-slate-500 uppercase">{k.replace(/_/g, ' ')}</span><p className="font-medium text-blue-900">{v}</p></div>)}
                  </div>
                </Section>
                <Section title="Family Details">
                  {(viewForm.family_details || []).map((f, i) => f.name && <p key={`fam-${f.name}-${i}`}>{f.name} — {f.relation} — {f.age_sex} — {f.occupation}</p>)}
                </Section>
                <Section title="Education">
                  {(viewForm.education || []).map((e, i) => e.qualification && <p key={`edu-${e.qualification}-${i}`}>{e.qualification} — {e.university} ({e.year}) — {e.percentage}% — {e.major_subject}</p>)}
                </Section>
                <Section title="Experience">
                  {(viewForm.experience || []).map((e, i) => e.organisation && <p key={`exp-${e.organisation}-${i}`}>{e.organisation} — {e.designation} ({e.from} to {e.to}) — {e.gross_salary}</p>)}
                </Section>
                <Section title="Emergency">
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(viewForm.emergency || {}).map(([k, v]) => v && <div key={k}><span className="text-xs text-slate-500 uppercase">{k.replace(/_/g, ' ')}</span><p className="font-medium">{v}</p></div>)}
                  </div>
                </Section>
                <Section title="Documents">
                  {Object.entries(viewForm.documents || {}).map(([type, doc]) => (
                    <div key={type} className="flex items-center gap-2 py-1">
                      <FileText size={14} className="text-blue-600" />
                      <span className="text-xs font-bold uppercase text-slate-600">{type}:</span>
                      <a href={`${BACKEND_URL}${doc.file_url}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-sm">{doc.file_name}</a>
                    </div>
                  ))}
                  {Object.keys(viewForm.documents || {}).length === 0 && <p className="text-slate-400">No documents uploaded</p>}
                </Section>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogContent className="rounded max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-red-800">Reject & Send Back for Resubmission</DialogTitle>
              <DialogDescription>Specify the reason for rejection (e.g., document deficiency, incomplete info).</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-3">
              <Textarea data-testid="reject-reason" value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Enter rejection reason..." className="rounded border-red-200 min-h-[100px]" />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setRejectOpen(false)} className="rounded">Cancel</Button>
                <Button data-testid="confirm-reject" onClick={() => { if (!rejectReason.trim()) { toast.error('Please provide a reason'); return; } handleStatusUpdate(rejectFormId, 'rejected', rejectReason); }} className="bg-red-600 hover:bg-red-700 text-white rounded">
                  <XCircle size={14} className="mr-1" />Reject & Send Back
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Document upload step
  if (step === 8) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Upload Documents</h1>
          <p className="text-slate-600">Please upload the required documents</p>
        </div>
        <div className="bg-white border border-blue-100 rounded shadow-sm p-6 space-y-4">
          {DOC_TYPES.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-4 p-3 bg-blue-50 rounded border border-blue-100">
              <FileText size={20} className="text-blue-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-bold text-blue-900 text-sm">{label}</p>
              </div>
              <Input data-testid={`upload-${key}`} type="file" accept=".pdf,.jpg,.jpeg,.png,.docx,.doc" onChange={(e) => { if (e.target.files[0]) handleUploadDoc(key, e.target.files[0]); }} className="w-64 text-sm" disabled={uploading[key]} />
            </div>
          ))}
          <Button data-testid="finish-onboarding-btn" onClick={() => { setStep(0); setFormId(null); fetchForms(); }} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold mt-4">Done</Button>
        </div>
      </div>
    );
  }

  // Multi-step form
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Joining Form</h1>
        <p className="text-slate-600">Step {step} of 7 — {STEPS[step - 1]}</p>
        <div className="flex gap-1 mt-3">
          {STEPS.slice(0, 7).map((s, i) => (
            <div key={s} className={`h-1.5 flex-1 rounded ${i < step ? 'bg-blue-600' : 'bg-slate-200'}`} />
          ))}
        </div>
      </div>
      <div className="bg-white border border-blue-100 rounded shadow-sm p-6">
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm font-bold text-blue-900 uppercase tracking-wider border-b border-blue-100 pb-2">Personal Information</p>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Full Name *" value={personal.full_name} onChange={(e) => setPersonal({ ...personal, full_name: e.target.value })} required />
              <InputField label="Father's Name" value={personal.fathers_name} onChange={(e) => setPersonal({ ...personal, fathers_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <InputField label="Date of Birth *" type="date" value={personal.dob} onChange={(e) => setPersonal({ ...personal, dob: e.target.value })} required />
              <InputField label="Weight" value={personal.weight} onChange={(e) => setPersonal({ ...personal, weight: e.target.value })} />
              <InputField label="Height" value={personal.height} onChange={(e) => setPersonal({ ...personal, height: e.target.value })} />
            </div>
            <InputField label="Current Residential Address *" value={personal.current_address} onChange={(e) => setPersonal({ ...personal, current_address: e.target.value })} required />
            <InputField label="Permanent Address" value={personal.permanent_address} onChange={(e) => setPersonal({ ...personal, permanent_address: e.target.value })} />
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Contact No. 1 *" value={personal.contact1} onChange={(e) => setPersonal({ ...personal, contact1: e.target.value })} required />
              <InputField label="Contact No. 2" value={personal.contact2} onChange={(e) => setPersonal({ ...personal, contact2: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <InputField label="Email *" type="email" value={personal.email} onChange={(e) => setPersonal({ ...personal, email: e.target.value })} required />
              <InputField label="Aadhaar No." value={personal.aadhaar_no} onChange={(e) => setPersonal({ ...personal, aadhaar_no: e.target.value })} />
              <InputField label="PAN No." value={personal.pan_no} onChange={(e) => setPersonal({ ...personal, pan_no: e.target.value })} />
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Marital Status</Label>
                <select value={personal.marital_status} onChange={(e) => setPersonal({ ...personal, marital_status: e.target.value })} className="mt-1 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm"><option value="">Select</option><option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option></select></div>
              <InputField label="Blood Group" value={personal.blood_group} onChange={(e) => setPersonal({ ...personal, blood_group: e.target.value })} />
              <InputField label="Designation" value={personal.designation} onChange={(e) => setPersonal({ ...personal, designation: e.target.value })} />
              <InputField label="Location" value={personal.location} onChange={(e) => setPersonal({ ...personal, location: e.target.value })} />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-blue-100 pb-2">
              <p className="text-sm font-bold text-blue-900 uppercase tracking-wider">Family Details</p>
              <Button size="sm" type="button" onClick={() => addRow(family, setFamily, EMPTY_FAMILY)} variant="outline" className="text-xs"><Plus size={12} className="mr-1" />Add Member</Button>
            </div>
            {family.map((f, i) => (
              <div key={`family-${i}`} className="grid grid-cols-5 gap-3 items-end">
                <InputField label="Name" value={f.name} onChange={(e) => updateRow(family, setFamily, i, 'name', e.target.value)} />
                <InputField label="Age / Sex" value={f.age_sex} onChange={(e) => updateRow(family, setFamily, i, 'age_sex', e.target.value)} />
                <InputField label="Relation" value={f.relation} onChange={(e) => updateRow(family, setFamily, i, 'relation', e.target.value)} />
                <InputField label="Occupation" value={f.occupation} onChange={(e) => updateRow(family, setFamily, i, 'occupation', e.target.value)} />
                <Button size="sm" variant="ghost" type="button" onClick={() => removeRow(family, setFamily, i)} className="text-red-500 h-10 mt-auto" disabled={family.length <= 1}><Trash2 size={14} /></Button>
              </div>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-blue-100 pb-2">
              <p className="text-sm font-bold text-blue-900 uppercase tracking-wider">Education Qualification</p>
              <Button size="sm" type="button" onClick={() => addRow(education, setEducation, EMPTY_EDU)} variant="outline" className="text-xs"><Plus size={12} className="mr-1" />Add</Button>
            </div>
            {education.map((e, i) => (
              <div key={`edu-${i}`} className="grid grid-cols-6 gap-3 items-end">
                <InputField label="Qualification" value={e.qualification} onChange={(ev) => updateRow(education, setEducation, i, 'qualification', ev.target.value)} />
                <InputField label="University / Institute" value={e.university} onChange={(ev) => updateRow(education, setEducation, i, 'university', ev.target.value)} />
                <InputField label="Year" value={e.year} onChange={(ev) => updateRow(education, setEducation, i, 'year', ev.target.value)} />
                <InputField label="% Marks" value={e.percentage} onChange={(ev) => updateRow(education, setEducation, i, 'percentage', ev.target.value)} />
                <InputField label="Major Subject" value={e.major_subject} onChange={(ev) => updateRow(education, setEducation, i, 'major_subject', ev.target.value)} />
                <Button size="sm" variant="ghost" type="button" onClick={() => removeRow(education, setEducation, i)} className="text-red-500 h-10 mt-auto" disabled={education.length <= 1}><Trash2 size={14} /></Button>
              </div>
            ))}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-blue-100 pb-2">
              <p className="text-sm font-bold text-blue-900 uppercase tracking-wider">Work Experience</p>
              <Button size="sm" type="button" onClick={() => addRow(experience, setExperience, EMPTY_EXP)} variant="outline" className="text-xs"><Plus size={12} className="mr-1" />Add</Button>
            </div>
            {experience.map((e, i) => (
              <div key={`exp-${i}`} className="border border-slate-200 rounded p-4 space-y-3">
                <div className="flex justify-between items-center"><p className="text-xs text-slate-500 font-bold">Experience #{i + 1}</p>
                  <Button size="sm" variant="ghost" type="button" onClick={() => removeRow(experience, setExperience, i)} className="text-red-500" disabled={experience.length <= 1}><Trash2 size={14} /></Button></div>
                <div className="grid grid-cols-3 gap-3">
                  <InputField label="Organisation" value={e.organisation} onChange={(ev) => updateRow(experience, setExperience, i, 'organisation', ev.target.value)} />
                  <InputField label="From" type="date" value={e.from} onChange={(ev) => updateRow(experience, setExperience, i, 'from', ev.target.value)} />
                  <InputField label="To" type="date" value={e.to} onChange={(ev) => updateRow(experience, setExperience, i, 'to', ev.target.value)} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <InputField label="Designation" value={e.designation} onChange={(ev) => updateRow(experience, setExperience, i, 'designation', ev.target.value)} />
                  <InputField label="Gross Salary" value={e.gross_salary} onChange={(ev) => updateRow(experience, setExperience, i, 'gross_salary', ev.target.value)} />
                  <InputField label="Reason for Leaving" value={e.reason_for_leaving} onChange={(ev) => updateRow(experience, setExperience, i, 'reason_for_leaving', ev.target.value)} />
                </div>
              </div>
            ))}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4">
            <p className="text-sm font-bold text-blue-900 uppercase tracking-wider border-b border-blue-100 pb-2">Last Position Held</p>
            <div className="grid grid-cols-3 gap-4">
              <InputField label="Designation" value={lastPosition.designation} onChange={(e) => setLastPosition({ ...lastPosition, designation: e.target.value })} />
              <InputField label="Organisation" value={lastPosition.organisation} onChange={(e) => setLastPosition({ ...lastPosition, organisation: e.target.value })} />
              <InputField label="Date of Joining" type="date" value={lastPosition.doj} onChange={(e) => setLastPosition({ ...lastPosition, doj: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <InputField label="Business" value={lastPosition.business} onChange={(e) => setLastPosition({ ...lastPosition, business: e.target.value })} />
              <InputField label="Annual Turnover" value={lastPosition.annual_turnover} onChange={(e) => setLastPosition({ ...lastPosition, annual_turnover: e.target.value })} />
              <InputField label="Gross Salary / Month" value={lastPosition.gross_salary} onChange={(e) => setLastPosition({ ...lastPosition, gross_salary: e.target.value })} />
            </div>
            <InputField label="Job Responsibility" value={lastPosition.job_responsibility} onChange={(e) => setLastPosition({ ...lastPosition, job_responsibility: e.target.value })} />
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Reporting To (Name)" value={lastPosition.reporting_to_name} onChange={(e) => setLastPosition({ ...lastPosition, reporting_to_name: e.target.value })} />
              <InputField label="Reporting To (Designation)" value={lastPosition.reporting_to_designation} onChange={(e) => setLastPosition({ ...lastPosition, reporting_to_designation: e.target.value })} />
            </div>
            <p className="text-sm font-bold text-blue-900 uppercase tracking-wider border-b border-blue-100 pb-2 mt-6">References (at least 2, not related)</p>
            {references.map((r, i) => (
              <div key={`ref-${i}`} className="grid grid-cols-3 gap-3">
                <InputField label={`Ref ${i + 1} — Name`} value={r.name} onChange={(e) => updateRow(references, setReferences, i, 'name', e.target.value)} />
                <InputField label="Address" value={r.address} onChange={(e) => updateRow(references, setReferences, i, 'address', e.target.value)} />
                <InputField label="Phone" value={r.phone} onChange={(e) => updateRow(references, setReferences, i, 'phone', e.target.value)} />
              </div>
            ))}
          </div>
        )}

        {step === 6 && (
          <div className="space-y-4">
            <p className="text-sm font-bold text-blue-900 uppercase tracking-wider border-b border-blue-100 pb-2">Additional Information</p>
            <InputField label="Physical Disabilities" value={additional.physical_disabilities} onChange={(e) => setAdditional({ ...additional, physical_disabilities: e.target.value })} />
            <InputField label="Marital Indebtness" value={additional.marital_indebtness} onChange={(e) => setAdditional({ ...additional, marital_indebtness: e.target.value })} />
            <InputField label="Court Proceedings (if any)" value={additional.court_proceedings} onChange={(e) => setAdditional({ ...additional, court_proceedings: e.target.value })} />
            <InputField label="Languages Known" value={additional.languages_known} onChange={(e) => setAdditional({ ...additional, languages_known: e.target.value })} />
            <InputField label="Specialized Training / Programs" value={additional.specialized_training} onChange={(e) => setAdditional({ ...additional, specialized_training: e.target.value })} />
            <InputField label="Related to any employee? (Name)" value={additional.related_to_employee} onChange={(e) => setAdditional({ ...additional, related_to_employee: e.target.value })} />
            <p className="text-sm font-bold text-blue-900 uppercase tracking-wider border-b border-blue-100 pb-2 mt-4">Previously Interviewed at HRassistance?</p>
            <div className="grid grid-cols-4 gap-4">
              <InputField label="Date" type="date" value={additional.interview_date} onChange={(e) => setAdditional({ ...additional, interview_date: e.target.value })} />
              <InputField label="Position" value={additional.interview_position} onChange={(e) => setAdditional({ ...additional, interview_position: e.target.value })} />
              <InputField label="Location" value={additional.interview_location} onChange={(e) => setAdditional({ ...additional, interview_location: e.target.value })} />
              <InputField label="Outcome" value={additional.interview_outcome} onChange={(e) => setAdditional({ ...additional, interview_outcome: e.target.value })} />
            </div>
          </div>
        )}

        {step === 7 && (
          <div className="space-y-4">
            <p className="text-sm font-bold text-blue-900 uppercase tracking-wider border-b border-blue-100 pb-2">Emergency Details</p>
            <div className="grid grid-cols-4 gap-4">
              <InputField label="Blood Group" value={emergency.blood_group} onChange={(e) => setEmergency({ ...emergency, blood_group: e.target.value })} />
              <InputField label="Allergic To" value={emergency.allergic_to} onChange={(e) => setEmergency({ ...emergency, allergic_to: e.target.value })} />
              <InputField label="Eyesight (Left)" value={emergency.eyesight_left} onChange={(e) => setEmergency({ ...emergency, eyesight_left: e.target.value })} />
              <InputField label="Eyesight (Right)" value={emergency.eyesight_right} onChange={(e) => setEmergency({ ...emergency, eyesight_right: e.target.value })} />
            </div>
            <InputField label="Any Major Illness" value={emergency.major_illness} onChange={(e) => setEmergency({ ...emergency, major_illness: e.target.value })} />
            <p className="text-sm font-bold text-blue-900 uppercase tracking-wider border-b border-blue-100 pb-2 mt-4">Emergency Contact Person</p>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Contact Person Name" value={emergency.contact_person} onChange={(e) => setEmergency({ ...emergency, contact_person: e.target.value })} />
              <InputField label="Phone" value={emergency.contact_phone} onChange={(e) => setEmergency({ ...emergency, contact_phone: e.target.value })} />
            </div>
            <InputField label="Address" value={emergency.contact_address} onChange={(e) => setEmergency({ ...emergency, contact_address: e.target.value })} />
            <div className="bg-amber-50 border border-amber-200 rounded p-4 mt-6">
              <p className="text-sm font-bold text-amber-900 uppercase mb-2">Declaration</p>
              <p className="text-xs text-slate-700">I declare that the information given herein above is true & correct to the best of my knowledge & belief & nothing material has been concealed. I understand that if the above information is found false or incorrect at any time during the course of my employment, my services will be terminated forthwith without any notice or compensation.</p>
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input type="checkbox" checked={declaration} onChange={(e) => setDeclaration(e.target.checked)} className="w-4 h-4 rounded border-amber-300" data-testid="declaration-checkbox" />
                <span className="text-sm font-bold text-amber-900">I accept the above declaration</span>
              </label>
            </div>
          </div>
        )}

        <div className="flex justify-between mt-6 pt-4 border-t border-slate-200">
          <Button variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step <= 1} className="rounded">Previous</Button>
          {step < 7 ? (
            <Button data-testid="next-step-btn" onClick={() => setStep(step + 1)} className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Next</Button>
          ) : (
            <Button data-testid="submit-form-btn" onClick={handleSubmitForm} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold">Submit & Upload Documents</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs font-bold text-blue-900 uppercase tracking-wider border-b border-blue-100 pb-1 mb-2">{title}</p>
      {children}
    </div>
  );
}
